import { Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { FiMediaStorageService } from '../media-bank/fi-media-storage.service';
import { FiImageSize } from '../types/image-size';
import { guid } from '../utils/guid';
import { ITextItemMediaModel } from './fi-text-item-media';
import { IMediaBankFileReference } from '../media-bank';
import { ITextItemLinkModel } from './fi-text-item-link';
import { MediaBankStorageType } from '../media-bank/media-bank-storage-type';
import { FiLinkToType } from './fi-link-to-type';
import { FileType, ObjectTypes } from '../types';
import { FrontendMode, MobileAppHelperBaseService } from '../fi-shared-services/mobile-app-helper-base.service';
import { FI_BACKEND_CONFIG, IFiBackendConfig } from '../fi-backend/fi-backend.service';
import { FiSignalRService } from '@fi-shared/fi-signal-r/fi-signal-r.service';
import { Subject } from 'rxjs';
import { MediaBankStateType } from '@fi-shared/media-bank/media-bank-state-type';
import { safeStartsWith } from '@fi-shared/utils/safe-starts-with';
import { TextItemMediaClickActionType } from '@fi-shared/types/text-item-media-click-action-type';
import { MediaViewerService } from '@fi-shared/media-viewer/media-viewer.service';
import { isDefined } from '@fi-shared/utils';
import { FiApplicationLanguageType } from '@fi-shared/fi-content-language-shared/fi-application-language-type';
import { LinkToTypesService } from '@fi-shared/link-to/link-to-types.service';
import { replaceVimeoNotAvailableContainerWithPreviewImage } from '../text-editor/text-editor-html-element';
import { FiVimeoService } from '../media-bank/fi-vimeo/fi-vimeo.service';
import { FiDisplayAs } from '../types/display-as';

@Injectable()
export class FiTextItemHelperService {
    private renderer: Renderer2;

    constructor(
        private mediaStorageService: FiMediaStorageService,
        @Inject(FI_BACKEND_CONFIG) private backendConfig: IFiBackendConfig,
        private fiSignalRService: FiSignalRService,
        private mobileAppService: MobileAppHelperBaseService,
        private mediaViewerService: MediaViewerService,
        private linkToTypesService: LinkToTypesService,
        private vimeoService: FiVimeoService,
        rendererFactory: RendererFactory2,
    ) {
        this.renderer = rendererFactory.createRenderer(null, null);
    }

    subscribeOnVimeoUpdate(element: HTMLElement, ngOnDestroy$: Subject<void>, updateCallback?: () => void) {
        this.fiSignalRService.vimeoVideosDataUpdatedEvent.subscribe((eventArgs) => {
            Array.from(element.querySelectorAll('div[ccm-text-item-media-model]')).forEach((textItemMediaElement) => {
                const modelStr = textItemMediaElement.getAttribute('ccm-text-item-media-model');
                const model: ITextItemMediaModel = JSON.parse(modelStr);
                let contentUpdated = false;
                if (
                    model?.file?.StorageType === MediaBankStorageType.Vimeo &&
                    eventArgs.VimeoIds.indexOf(model.file.PhysicalFileName) !== -1
                ) {
                    // update changed on for vimeo media references and reload previews
                    model.file.ChangedOn = new Date().toISOString();
                    const size = this.getSize(element);

                    const newSrc = this.mediaStorageService.getMediaFileUrlByFileName(
                        model.file.FullName,
                        size,
                        model.file.ChangedOn,
                    );
                    replaceVimeoNotAvailableContainerWithPreviewImage(model, textItemMediaElement, newSrc);
                    contentUpdated = true;
                }

                if (contentUpdated && updateCallback) {
                    // update model in ContenteditableModelDirective
                    updateCallback();
                }
            });
        }, ngOnDestroy$);
    }

    updateTextItemLinks(renderer: Renderer2, nativeElement: HTMLElement) {
        Array.prototype.forEach.call(
            nativeElement.querySelectorAll('a[ccm-text-item-link], CCM-TEXT-ITEM-LINK'),
            (linkElement) => {
                let model: ITextItemLinkModel = JSON.parse(linkElement.getAttribute('text-item-model'));

                // due to changes in logic, this element can be A or CCM-TEXT-ITEM-LINK (old version)

                let aEl: HTMLAnchorElement;
                if (linkElement.tagName == 'A') {
                    aEl = linkElement;
                } else {
                    aEl = linkElement.querySelector('A');
                    if (!aEl) {
                        aEl = renderer.createElement('A');
                        renderer.appendChild(linkElement, aEl);
                    } else if (aEl.getAttribute('text-item-model')) {
                        model = JSON.parse(aEl.getAttribute('text-item-model'));
                    }
                }

                if (model && model.Type == FiLinkToType.WebAddress && this.isExternal(aEl)) {
                    // todo use FiLinkToType and resolve circular dependency
                    renderer.setAttribute(aEl, 'target', '_blank');
                }
                const url = this.getUrlForItemLinkModel(model);
                if (url) {
                    renderer.setAttribute(aEl, 'href', url);
                }
            },
        );

        // update links for images with linkTo
        // https://chainformation.atlassian.net/browse/FI-4881
        Array.prototype.forEach.call(
            nativeElement.querySelectorAll('img[ccm-text-item-media-model]'),
            (elem: HTMLImageElement) => {
                if (elem.parentElement && elem.parentElement.tagName === 'A') {
                    let aEl: HTMLAnchorElement = elem.parentElement as HTMLAnchorElement;
                    if (aEl) {
                        const model: ITextItemMediaModel = JSON.parse(elem.getAttribute('ccm-text-item-media-model'));
                        if (model && model.linkTo) {
                            const url = this.getUrlForItemLinkModel(model.linkTo);
                            if (url) {
                                renderer.setAttribute(aEl, 'href', url);
                            }
                        }
                    }
                }
            },
        );
    }

    getUrlForItemLinkModel(model: ITextItemLinkModel): string {
        return this.linkToTypesService.getUrlForItemLinkModel(model);
    }

    isExternal(link: HTMLAnchorElement) {
        if (link.hostname === window.location.hostname) return false;

        const hostName = (() => {
            if (this.mobileAppService.frontEndMode !== FrontendMode.MobileApp) {
                return window.location.hostname;
            }
            try {
                const urlObj = new URL(this.backendConfig.basePath);
                return urlObj.hostname;
            } catch (ex) {
                return '';
            }
        })();
        return link.hostname !== hostName;
    }

    isFullInternal(link: HTMLAnchorElement) {
        try {
            const urlObj = new URL(this.backendConfig.basePath);
            return urlObj.hostname === link.hostname;
        } catch (e) {}
        return false;
    }

    updateTextItemMediaImages(element: HTMLElement, mediaBanKFileReferences?: IMediaBankFileReference[]) {
        // todo remove template-item-text-image

        let textItemMediaElements = element.querySelectorAll(
            'img[ccm-text-item-media-model], a[ccm-text-item-media-model], div[ccm-text-item-media-model]',
        );
        if (!textItemMediaElements || !textItemMediaElements.length) {
            return;
        }

        const size = this.getSize(element);
        for (let i = 0; i < textItemMediaElements.length; i++) {
            const textItemMediaElement = textItemMediaElements.item(i);
            const modelStr = textItemMediaElement.getAttribute('ccm-text-item-media-model');
            const model: ITextItemMediaModel = JSON.parse(modelStr);

            if (!model || !model.file || !model.file.FullName) {
                continue;
            }

            // note: special case for corrupted image anchors https://chainformation.atlassian.net/browse/FI-5040
            if (
                model.hasOwnProperty('linkTo') &&
                textItemMediaElements[i].parentElement.nodeName.toLowerCase() !== 'a'
            ) {
                this.renderer.setStyle(textItemMediaElements[i], 'cursor', 'pointer');
            }

            this.updateUpcloudImageData(mediaBanKFileReferences, textItemMediaElement, model, size);
            this.updateVimeoMedia(mediaBanKFileReferences, textItemMediaElement, model);
        }
    }

    private updateUpcloudImageData(
        mediaBankFileReferences: IMediaBankFileReference[] = [],
        textItemMediaElement: Element,
        model: ITextItemMediaModel,
        size: FiImageSize,
    ) {
        if (model.file.StorageType !== MediaBankStorageType.Vimeo) {
            // some old image links point to old upcloud object storage, so we need to fix it
            const mediaBankFileReference = mediaBankFileReferences.find(
                (r) => r.MediaBankFileId === model.file.MediaBankFileId,
            );
            const changedOn = mediaBankFileReference ? mediaBankFileReference.ChangedOn : model.file.ChangedOn;
            const newSrc = this.mediaStorageService.getMediaFileUrlByFileName(model.file.FullName, size, changedOn);
            const imgElement = this.getImgElement(textItemMediaElement);
            if (imgElement) {
                imgElement.setAttribute('src', newSrc);
                imgElement.setAttribute('draggable', 'false');
            }
        }
    }

    private updateVimeoMedia(
        mediaBankFileReferences: IMediaBankFileReference[] = [],
        textItemMediaElement: Element,
        model: ITextItemMediaModel,
    ): void {
        const mediaBankFileReference = mediaBankFileReferences.find(
            (r) => r.MediaBankFileId === model.file.MediaBankFileId,
        );
        if (
            !isDefined(mediaBankFileReference) ||
            model.file.StorageType !== MediaBankStorageType.Vimeo ||
            model.displayAs !== FiDisplayAs.Image.Id
        ) {
            return;
        }

        this.vimeoService.getVideoInfo(mediaBankFileReference.PhysicalFileName).then(({ isAvailable }) => {
            if (isAvailable) {
                this.setReadyState(textItemMediaElement);
            }
        });
    }

    updateTemplateItemTextImage(nativeElement, existingReferences: IMediaBankFileReference[]) {
        if (!existingReferences) return;

        Array.from(nativeElement.querySelectorAll('[ccm-text-item-media-model]')).forEach((element: HTMLElement) => {
            const attr = element.getAttribute('ccm-text-item-media-model');
            if (attr) {
                const mediaModel: ITextItemMediaModel = JSON.parse(attr);
                if (
                    mediaModel &&
                    mediaModel.file &&
                    mediaModel.file.MediaBankFileId /*&& mediaModel.file.StorageType !== MediaBankStorageType.Vimeo*/ &&
                    (mediaModel.file.FileType == FileType.Image || mediaModel.file.FileType == FileType.Movie)
                ) {
                    var existingReference = existingReferences.find(
                        (ref) => ref.MediaBankFileId === mediaModel.file.MediaBankFileId,
                    );

                    if (
                        existingReference &&
                        existingReference.PhysicalFileName &&
                        mediaModel.file.FullName !== existingReference.PhysicalFileName + existingReference.Extension
                    ) {
                        // update data for templateItemTextImage directive
                        //const templateItemTextImageAttr = element.getAttribute('template-item-text-image');
                        element.setAttribute(
                            'template-item-text-image',
                            existingReference.PhysicalFileName + existingReference.Extension,
                        );
                        // update data for contenteditable directive (when editing)
                        var url = this.mediaStorageService.getMediaFileUrl(existingReference, {
                            size: FiImageSize.Large,
                        });
                        element.setAttribute('src', url);
                        //udate data for ccmTextItemMediaEditController
                        mediaModel.file.FullName = existingReference.PhysicalFileName + existingReference.Extension;
                        element.setAttribute('ccm-text-item-media-model', JSON.stringify(mediaModel));
                    }
                    element.setAttribute('draggable', 'false');
                }
            }
        });
    }

    getSize(element: HTMLElement) {
        const width = element.getBoundingClientRect().width;
        if (width > 0 && width <= 700) return FiImageSize.Medium;
        return FiImageSize.Large;
    }

    updateIFrameSourcesAndStyles(nativeElement: HTMLElement) {
        Array.from(nativeElement.querySelectorAll('iframe[ccm-text-item-media-model], ccm-text-item-embed')).forEach(
            (element: HTMLElement) => {
                this.updateIFrameSourcesAndStylesOfEmbedElement(element);
            },
        );
    }

    updateIFrameSourcesAndStylesOfEmbedElement(element: HTMLElement) {
        const cb = guid();
        if (element.matches('iframe[ccm-text-item-media-model]')) {
            let src = element.getAttribute('src');
            const idx = src.indexOf('?');
            if (idx !== -1) {
                src = src.substr(0, idx);
            }
            src += `?cb=${cb}`;
            element.setAttribute('src', src);
        }
        if (element.matches('ccm-text-item-embed')) {
            try {
                // https://chainformation.atlassian.net/browse/FI-2790
                const model: ITextItemLinkModel = JSON.parse(element.getAttribute('text-item-model'));
                const iframeSizeStyles = this.getIframeSizeStyles(model.EmbedText);
                element.innerHTML = model.EmbedText;
                this.executeScripts(element);
                if (!!iframeSizeStyles.length) {
                    (element.firstChild as HTMLElement).setAttribute('style', iframeSizeStyles);
                }
            } catch {}
        }
    }

    getIframeSizeStyles(embedText: string): string {
        const startIndexOfWidth: number = embedText.indexOf(' width="');
        let styleSize: string = '';
        if (startIndexOfWidth !== -1) {
            const widthStringStart: string = embedText.substring(startIndexOfWidth).replace(' width="', '');
            const width: string = widthStringStart.substring(0, widthStringStart.indexOf('"')).replace('px', '');
            styleSize = styleSize.concat(`width: ${width}px;`);
        }
        const startIndexOfHeight: number = embedText.indexOf(' height="');
        if (startIndexOfHeight !== -1) {
            const heightStringStart: string = embedText.substring(startIndexOfHeight).replace(' height="', '');
            const height: string = heightStringStart.substring(0, heightStringStart.indexOf('"')).replace('px', '');
            styleSize = styleSize.concat(`height: ${height}px;`);
        }

        return styleSize;
    }

    removeNonExistingMediaReferences(nativeElement: HTMLElement, mediaReferences: IMediaBankFileReference[]) {
        if (!mediaReferences) return;

        Array.from(nativeElement.querySelectorAll('[ccm-text-item-media-model]')).forEach((element: HTMLElement) => {
            const attr = element.getAttribute('ccm-text-item-media-model');
            if (attr) {
                const mediaModel = JSON.parse(attr);
                if (
                    mediaModel &&
                    mediaModel.file &&
                    mediaModel.file.MediaBankFileId &&
                    (mediaModel.file.FileType == FileType.Image || mediaModel.file.FileType == FileType.Movie)
                ) {
                    const existingReference = mediaReferences.find(
                        (ref) => ref.MediaBankFileId === mediaModel.file.MediaBankFileId,
                    );
                    if (!existingReference) {
                        // https://chainformation.atlassian.net/browse/FI-2192
                        element.style.display = 'none';
                        //element.parentNode.removeChild(element);
                        console.log('removed', mediaModel, mediaReferences);
                    } else {
                        element.style.display = '';
                    }
                }
            }
        });
    }

    private setReadyState(videoContainerElement: Element) {
        const modelStr = videoContainerElement.getAttribute('ccm-text-item-media-model');
        const model: ITextItemMediaModel = JSON.parse(modelStr);
        replaceVimeoNotAvailableContainerWithPreviewImage(
            model,
            videoContainerElement,
            this.mediaStorageService.getMediaFileUrlByFileName(
                model.file.FullName,
                FiImageSize.Large,
                model.file.ChangedOn,
            ),
        );
    }

    private getImgElement(textItemMediaElement: Element) {
        const imgElement = textItemMediaElement.matches('img')
            ? textItemMediaElement
            : textItemMediaElement.querySelector('img');
        return imgElement;
    }

    executeScripts(targetElement: HTMLElement) {
        // https://chainformation.atlassian.net/browse/FI-4837
        // execute scripts https://stackoverflow.com/questions/2592092/executing-script-elements-inserted-with-innerhtml
        Array.from(targetElement.querySelectorAll('script')).forEach((oldScript: HTMLScriptElement) => {
            const newScript: HTMLScriptElement = document.createElement('script');
            Array.from(oldScript.attributes).forEach((attr: Attr) => newScript.setAttribute(attr.name, attr.value));
            newScript.appendChild(document.createTextNode(oldScript.innerHTML));
            oldScript.parentNode.replaceChild(newScript, oldScript);
        });
    }

    onTextItemClick($event: MouseEvent, mediaBankFileReferences: IMediaBankFileReference[], skipStatisticsSave?: boolean) {
        // todo refactor, encapsulate all ccm-text-item-link, text-item-model logic in one service
        const targetElement = this.getTextItemModelElement($event.target as HTMLElement);
        if (!isDefined(targetElement)) return;
        /*if (targetElement.matches('a[ccm-text-item-link]')) {
            const model: ITextItemLinkModel = JSON.parse(targetElement.getAttribute('text-item-model'));
            const linkType = this.linkToTypesService.TypesMap[model.Type];

            if (linkType && linkType.LoadItem) {
                $event.preventDefault();
            }
        } else */
        if (targetElement.matches('[ccm-text-item-media-model]')) {
            const model: ITextItemMediaModel = JSON.parse(targetElement.getAttribute('ccm-text-item-media-model'));
            if (
                !model.linkTo &&
                (!model.onClickActionType || model.onClickActionType === TextItemMediaClickActionType.EnlargedView)
            ) {
                this.openCommonPreviewDialog(model, mediaBankFileReferences, skipStatisticsSave);
            }
        } else if (targetElement.matches('[fi-vimeo-preview-image]') || targetElement.matches('[fi-vimeo-video-preview-image]')) {
            const model: ITextItemMediaModel = JSON.parse(
                targetElement.parentElement.getAttribute('ccm-text-item-media-model'),
            );
            this.openCommonPreviewDialog(model, mediaBankFileReferences, skipStatisticsSave);
        }
    }

    private openCommonPreviewDialog(model: ITextItemMediaModel, mediaBankFileReferences: IMediaBankFileReference[], skipStatisticsSave?: boolean) {
        const mediaBankFileReference: IMediaBankFileReference = mediaBankFileReferences
            ? mediaBankFileReferences.find((r) => r.MediaBankFileId === model.file.MediaBankFileId)
            : undefined;
        const changedOn = mediaBankFileReference?.ChangedOn || model.file?.ChangedOn;
        const mediaBankFileUrl = this.mediaStorageService.getMediaFileUrlByFileName(
            model.file.FullName,
            FiImageSize.Large,
            changedOn,
        );
        this.mediaViewerService.Show(mediaBankFileUrl, undefined, [model.file], undefined, skipStatisticsSave);
    }

    private getTextItemModelElement(element: HTMLElement): HTMLElement {
        if (element.matches('[ccm-text-item-media-model], [fi-vimeo-preview-image]')) return element;
        if (element.parentElement.matches('[ccm-text-item-media-model], [fi-vimeo-preview-image]'))
            return element.parentElement;

        return;
    }

    getDirRtlAttr(contentLanguageId: FiApplicationLanguageType) {
        if (contentLanguageId === FiApplicationLanguageType.Arabic) {
            return 'rtl';
        }
        return undefined;
    }

    getTextAlignRtlAttr(contentLanguageId: FiApplicationLanguageType) {
        if (contentLanguageId === FiApplicationLanguageType.Arabic) {
            return 'right';
        }
        return undefined;
    }
}
